/**
 * API global de notificaciones (toasts / “globos”).
 * Usar notify() en código nuevo; window.alert se redirige aquí automáticamente.
 */

export type NotifySeverity = 'success' | 'error' | 'info' | 'warning';

export interface NotifyPayload {
  id: string;
  message: string;
  severity: NotifySeverity;
  title?: string;
  durationMs?: number;
}

type Listener = (payload: NotifyPayload) => void;

let listener: Listener | null = null;
let seq = 0;

/** Quita URLs locales y restos feos de mensajes de API/alert. */
export function sanitizeNotifyText(raw: unknown): string {
  let text = String(raw ?? '').trim();
  if (!text) return '';
  text = text
    .replace(/https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/[^\s]*)?/gi, '')
    .replace(/\b(localhost|127\.0\.0\.1)(:\d+)?(\/[^\s]*)?/gi, '')
    .replace(/\s*[·|]\s*(?=\s|$)/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([.,;:!?])/g, '$1')
    .trim();
  return text;
}

export function inferNotifySeverity(message: string): NotifySeverity {
  const m = message.toLowerCase();
  if (
    /\b(error|falló|fallo|no se pudo|no se puede|inválid|invalid|prohibid|denegad|obligatori|debe |debe\b)/i.test(
      m
    )
  ) {
    return /obligatori|debe |seleccione|ingrese|complete/.test(m) ? 'warning' : 'error';
  }
  if (/\b(éxito|exitos|correctamente|completad|guardad|enviad|cread|actualiz|anulad|registrad|descargad)/i.test(m)) {
    return 'success';
  }
  return 'info';
}

export function registerNotifyListener(fn: Listener | null): void {
  listener = fn;
}

export function notify(
  message: unknown,
  severity?: NotifySeverity,
  opts?: { title?: string; durationMs?: number }
): void {
  const clean = sanitizeNotifyText(message);
  if (!clean) return;
  const payload: NotifyPayload = {
    id: `n-${Date.now()}-${++seq}`,
    message: clean,
    severity: severity ?? inferNotifySeverity(clean),
    title: opts?.title,
    durationMs: opts?.durationMs,
  };
  if (listener) {
    listener(payload);
  } else if (typeof window !== 'undefined') {
    // Fallback antes de montar el provider
    window.console?.info?.(`[${payload.severity}]`, payload.message);
  }
}

export function notifySuccess(message: unknown, title?: string): void {
  notify(message, 'success', { title });
}

export function notifyError(message: unknown, title?: string): void {
  notify(message, 'error', { title });
}

export function notifyWarning(message: unknown, title?: string): void {
  notify(message, 'warning', { title });
}

export function notifyInfo(message: unknown, title?: string): void {
  notify(message, 'info', { title });
}

/** Sustituye alert() nativo por globos. */
export function installAlertAsNotify(): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const original = window.alert.bind(window);
  window.alert = ((message?: any) => {
    notify(message);
  }) as typeof window.alert;
  return () => {
    window.alert = original;
  };
}

/**
 * Confirmación con mensaje claro (sigue usando diálogo nativo por ahora,
 * con texto sanitizado). Preferir Dialog MUI en pantallas nuevas.
 */
export function confirmAction(message: unknown, title?: string): boolean {
  const clean = sanitizeNotifyText(message);
  const text = title ? `${sanitizeNotifyText(title)}\n\n${clean}` : clean;
  if (typeof window === 'undefined') return false;
  return window.confirm(text || '¿Confirmar acción?');
}
